import React, { useState, useEffect, useCallback, ReactElement } from 'react';
import { strings } from 'src/localization';
import { DateTime } from '@reynolds/datetime';
import { RepairOrder, IDMMedia, ascCtaInteractionInput } from 'src/models';
import css from '../ServiceDocs.module.scss';
import { IconRemove, IconRemoveAudi, IconBackArrow, IconDownload, IconRDropDownArrow,
    IconLDropDownArrow } from 'src/content/icons';
import { ScrollBar, Dialog, Button } from '@framework-controls/core';
import { THEME } from 'src/constants/ThemeNames';
import ServiceDocsMediaItem from './ServiceDocsMediaItem/ServiceDocsMediaItem';
import ServiceDocsMediaNav from './ServiceDocsMediaNav/ServiceDocsMediaNav';
import SaveAs from 'src/components/SaveAs/SaveAs';
import moment from 'moment';
import 'moment/min/locales.min';
import { ascCtaInteraction } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

type iconNavItem = {
    type: string;
    iconFilled: boolean;
    onClick: (index: number) => void;
}

export interface IProps {
    selectedRO: RepairOrder;
    previewFlag: string;
    dialogTitle: string;
    locale: string;
    setIVModalFlag: (flag: boolean) => void;
    IVModalFlag: boolean;
    idmDocs: any;
    idmFile: string;
    logDocsHistory: (keyRO: string, action: string, docType: string, details: string, idmUrl: string) => void;
    addCUC: (cucNum: number) => void;
    getIDMdoc: (docUUID: string, elementUUID: string) => void;
    clearIdmFile: () => void;
    theme: string;
}

const ServiceDocsMediaBox = (props: IProps) => {
    const [fullImageFlag, setFullImageFlag] = useState(false);
    // current full image uses the index
    const [currentFullImg, setCurrentFullImage] = useState<number | null>(null);
    const [imageVidArray, setImageVidArray] = useState<ReactElement[] | null>([]);
    const [imageCheckArray, setImageCheckArray] = useState<boolean[]>([]);
    const [sortedIndexOrder, setSortedIndexOrder] = useState<number[]>([]);
    const [sortJobSeqBreakdown, setSortJobSeqBreakdown] = useState<number[]>([]);
    const [saveAsDownloadList, setSaveAsDownloadList] = useState<string[]>([]);
    const [saveAsDefaultFileNames, setSaveAsDefaultFileNames] = useState<string[]>([]);
    const [saveAsOpenFlag, setSaveAsOpenFlag] = useState<boolean>(false);
    const [startPosition, setStartPosition] = useState(0);
    const [endPosition, setEndPosition] = useState(0);
    const [gettingImgSave, setGettingImgSave] = useState<boolean>(false);
    const [gettingImgSaveDone, setGettingImgSaveDone] = useState<boolean>(false);
    const [saveImgIndex, setSaveImgIndex] = useState<number>(-1);
    const [downloadStart, setDownloadStart] = useState<boolean>(false);
    const [downloadFullStart, setDownloadFullStart] = useState<boolean>(false);
    const [saveAsIndexList, setSaveAsIndexList]= useState<number[]>([]);


    const processingDate = new Date(props.selectedRO.RODate);
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const roDate = processingUTCDate.format('MMMM D, YYYY');


    function touchStartHandler(event) {
        setStartPosition(event.changedTouches[0].clientX);
    }

    function touchMoveHandler(event) {
        setEndPosition(event.changedTouches[0].clientX);
    }

    function touchEndHandler() {
        const threshold = 20;
        const distance = endPosition - startPosition;

        if (endPosition !== 0 && (Math.abs(distance) > threshold)) {
            distance > 0 ? arrowMove('L') : arrowMove('R');
        }

        setStartPosition(0);
        setEndPosition(0);
    }

    const onToggle = useCallback((index: number, checked: boolean, e:any) => {
        const checkedArray = [...imageCheckArray];
        checkedArray[index] = checked;
        setImageCheckArray(checkedArray);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: '',
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'checkbox',
            eventAction: 'click',
            eventActionResult: checked === true ? 'check' : 'uncheck',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, [], '');
    }, [imageCheckArray]);

    const arrowMove = useCallback((direction: string) => {
        if (direction === 'L' && sortedIndexOrder && (currentFullImg || currentFullImg===0)) {
            const newSelection = (sortedIndexOrder.findIndex(result => result === currentFullImg)-1);
            if (newSelection > -1) {
                setCurrentFullImage(sortedIndexOrder[newSelection]);
                // log doc history
                let logUrl = '';
                if (props.selectedRO.IDMPics && props.selectedRO.IDMPics[sortedIndexOrder[newSelection]].url) {
                    logUrl = props.selectedRO.IDMPics[sortedIndexOrder[newSelection]].url;
                }
                if (!props.previewFlag) {
                    props.logDocsHistory(props.selectedRO.RONum, 'DV', 'IV', '', logUrl);
                }
                // add cuc 869 if an image
                if (props.selectedRO.IDMPics && props.selectedRO.IDMPics[sortedIndexOrder[newSelection]].urlType === 'I' &&
                !props.previewFlag) {
                    props.addCUC(869);
                }
            }
        }
        if (direction === 'R' && sortedIndexOrder && (currentFullImg || currentFullImg===0)) {
            const newSelection = (sortedIndexOrder.findIndex(result => result === currentFullImg)+1);
            if (newSelection < sortedIndexOrder.length) {
                setCurrentFullImage(sortedIndexOrder[newSelection]);
                // log doc history
                let logUrl = '';
                if (props.selectedRO.IDMPics && props.selectedRO.IDMPics[sortedIndexOrder[newSelection]].url) {
                    logUrl = props.selectedRO.IDMPics[sortedIndexOrder[newSelection]].url;
                }
                if (!props.previewFlag) {
                    props.logDocsHistory(props.selectedRO.RONum, 'DV', 'IV', '', logUrl);
                }
                // add cuc 869 if an image
                if (props.selectedRO.IDMPics && props.selectedRO.IDMPics[sortedIndexOrder[newSelection]].urlType === 'I' &&
                !props.previewFlag) {
                    props.addCUC(869);
                }
            }
        }
    }, [sortedIndexOrder, currentFullImg, props.selectedRO.RONum, props.selectedRO.IDMPics, props.previewFlag,
        props.logDocsHistory, props.addCUC]);

    const saveAsCloseHandler = useCallback(() => {
        setSaveAsOpenFlag(false);
        setSaveAsIndexList([]);
        setSaveAsDownloadList([]);
        setSaveAsDefaultFileNames([]);
        props.clearIdmFile();
        if (props.selectedRO && props.selectedRO.IDMPics) {
            const tempImgChk: boolean[] = [];
            props.selectedRO.IDMPics.forEach(() => {
                tempImgChk.push(false);
            });
            setImageCheckArray(tempImgChk);
        }
    }, [props.selectedRO, props.clearIdmFile]);

    // Audi only: Enter/Space on the close wrapper runs the same close as onClick.
    const thumbnailCloseKeyHandler = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            props.setIVModalFlag(false);
        }
    }, [props.setIVModalFlag]);

    // close handler for the full image view
    const galleryCloseHandler = useCallback(() => {
        props.setIVModalFlag(false);
        setFullImageFlag(false);
    }, [props.setIVModalFlag]);

    // Audi only: Enter/Space on the gallery close wrapper runs galleryCloseHandler.
    const galleryCloseKeyHandler = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            galleryCloseHandler();
        }
    }, [galleryCloseHandler]);

    // download handler for thumbnail view
    const downloadHandler = useCallback(() => {
        const anyCheckMarked = imageCheckArray.findIndex(check => check === true) !== -1 ? true:false;
        if (anyCheckMarked && !props.previewFlag) {
            // need to loop for each checked file.
            const tempIndexList: number[] = [];
            imageCheckArray.forEach((x, index) => {
                if (x && props.selectedRO.IDMPics) {
                    tempIndexList.push(index);
                }
            });
            setDownloadStart(true);
            setSaveAsIndexList(tempIndexList);
        }
    }, [imageCheckArray, props.selectedRO, props.previewFlag]);

    // thumbnail view download initialization
    useEffect(() => {
        if (downloadStart) {
            setGettingImgSave(true);
            setGettingImgSaveDone(false);
            setSaveAsDownloadList([]);
            setSaveAsDefaultFileNames([]);
            setSaveImgIndex(saveAsIndexList.length-1);
            setDownloadStart(false);
        }
    }, [downloadStart, saveAsIndexList]);

    // gets the blobs thumbnail view
    useEffect(() => {
        if (gettingImgSave && saveImgIndex >= 0 && props.selectedRO.IDMPics) {
            const currentImgLocation = saveAsIndexList[saveImgIndex];
            const ImgIndex = props.idmDocs?.data?.queryDocument?.findIndex(d => d.doc_uuid ===
                props.selectedRO.IDMPics![currentImgLocation].docId);
            if (ImgIndex && ImgIndex > -1) {
                const docUuid = props.idmDocs.data.queryDocument[ImgIndex].doc_uuid;
                const elementUuid = props.idmDocs.data.queryDocument[ImgIndex].elements?.[0]?.element_uuid;
                props.getIDMdoc(docUuid, elementUuid);
            }
        }
    }, [gettingImgSave, saveImgIndex, props.idmDocs, props.selectedRO, props.getIDMdoc, saveAsIndexList]);

    // stores blobs thumbnail view
    useEffect(() => {
        if (gettingImgSave && props.idmFile && saveImgIndex >= 0) {
            const saveAsUrls: string[] = saveAsDownloadList;
            saveAsUrls[saveImgIndex] = (props.idmFile);
            setSaveAsDownloadList(saveAsUrls);
            props.clearIdmFile();
            setSaveImgIndex(saveImgIndex - 1);
            if (saveImgIndex-1 < 0) {
                setGettingImgSaveDone(true);
            }
        }
    }, [gettingImgSave, saveImgIndex, props.idmFile, saveAsDownloadList, props.clearIdmFile]);

    // finishes prep and opens Save As
    useEffect(() => {
        if (saveAsIndexList.length > 0 && saveImgIndex <= 0 && gettingImgSaveDone) {
            const saveAsDefFileNames: string[] = [];
            saveAsIndexList.forEach((x) => {
                const opDesc = props.selectedRO.IDMPics![x].opDesc ?
                    props.selectedRO.IDMPics![x].opDesc:strings.Unassigned;
                const sortedPos = sortedIndexOrder.findIndex(result => result === x);
                const seqentialNum = sortJobSeqBreakdown[sortedPos] + 1;
                const fileNameDate = new DateTime(new Date(props.selectedRO.RODate)).format('MMDDYY');
                const imgFileName = opDesc + seqentialNum + '_' + fileNameDate;
                saveAsDefFileNames.push(imgFileName);
            });
            // set Save As dialog props and open it.
            setSaveAsDefaultFileNames(saveAsDefFileNames);
            setSaveAsOpenFlag(true);
            setGettingImgSaveDone(false);
        }
    }, [props.selectedRO, saveImgIndex, gettingImgSaveDone, saveAsIndexList, sortJobSeqBreakdown, sortedIndexOrder]);

    // download handler for the full image view
    const downloadFullHandler = useCallback(() => {
        if (props.selectedRO.IDMPics && (currentFullImg || currentFullImg === 0) && props.previewFlag !== 'p') {
            props.clearIdmFile();
            setSaveAsDownloadList([]);
            setSaveAsDefaultFileNames([]);
            setDownloadFullStart(true);
            const ImgIndex = props.idmDocs?.data?.queryDocument?.findIndex(d => d.doc_uuid ===
                props.selectedRO.IDMPics![currentFullImg].docId);
            if (ImgIndex && ImgIndex > -1) {
                const docUuid = props.idmDocs.data.queryDocument[ImgIndex].doc_uuid;
                const elementUuid = props.idmDocs.data.queryDocument[ImgIndex].elements?.[0]?.element_uuid;
                props.getIDMdoc(docUuid, elementUuid);
            }
        }
    }, [currentFullImg, props.previewFlag, props.selectedRO, props.clearIdmFile, props.getIDMdoc, props.idmDocs]);

    // preps and opens the save as modal for full image view download
    useEffect(() => {
        if (downloadFullStart && props.idmFile && (currentFullImg || currentFullImg === 0) && props.selectedRO.IDMPics) {
            const saveAsUrls: string[] = [];
            const saveAsDefFileNames: string[] = [];
            const imgUrl = props.idmFile;
            const opDesc = props.selectedRO.IDMPics[currentFullImg].opDesc ?
                props.selectedRO.IDMPics[currentFullImg].opDesc:strings.Unassigned;
            const sortedPos = sortedIndexOrder.findIndex(result => result === currentFullImg);
            const seqentialNum = sortJobSeqBreakdown[sortedPos] + 1;
            const fileNameDate = new DateTime(new Date(props.selectedRO.RODate)).format('MMDDYY');
            const imgFileName = opDesc + seqentialNum + '_' + fileNameDate;
            saveAsUrls.push(imgUrl);
            saveAsDefFileNames.push(imgFileName);
            // set Save As dialog props and open it.
            setSaveAsDownloadList(saveAsUrls);
            setSaveAsDefaultFileNames(saveAsDefFileNames);
            setSaveAsOpenFlag(true);
            setDownloadFullStart(false);
        }
    }, [props.selectedRO, props.idmFile, sortJobSeqBreakdown, currentFullImg, downloadFullStart, sortedIndexOrder]);

    // build the check array
    useEffect(() => {
        if (props.selectedRO && props.selectedRO.IDMPics) {
            const tempImgChk: boolean[] = [];
            props.selectedRO.IDMPics.forEach(() => {
                tempImgChk.push(false);
            });
            setImageCheckArray(tempImgChk);
        }
    }, [props.selectedRO]);

    const imageJobStyles = classNames(
        css.imageJob,
        {
            [css.imageJobBMW]: props.theme === 'bmw',
        },
    );

    // organize the images by job
    useEffect(() => {
        if (props.selectedRO && props.selectedRO.IDMPics && props.selectedRO.IDMPics.length > 0) {
            const idmPics = [...props.selectedRO.IDMPics];
            // Sorting Order -> Pending Red Items, Pending Items, Job Red groups, job groups, unassigned.
            // First break apart pending items and job groups
            const firstBreakPending: IDMMedia[] = [];
            const firstBreakJobs: IDMMedia[] = [];
            const firstBreakUnassigned: IDMMedia[] = [];
            // see if all images are unassigned
            let allUnassigned = true;
            idmPics.forEach((x) => {
                switch (x.jobOrPending) {
                    case ('I'): {
                        firstBreakPending.push(x);
                        allUnassigned = false;
                        break;
                    }
                    case ('J'): {
                        firstBreakJobs.push(x);
                        allUnassigned = false;
                        break;
                    }
                    default: {
                        firstBreakUnassigned.push(x);
                    }
                }
            });
            // Determine if red item or normal item
            const redPending: IDMMedia[] = [];
            const redJobs: IDMMedia[] = [];
            const normPending: IDMMedia[] = [];
            const normJobs: IDMMedia[] = [];
            firstBreakPending.forEach((x) => {
                switch (x.redItem) {
                    case ('Y'): {
                        redPending.push(x);
                        break;
                    }
                    default: {
                        normPending.push(x);
                    }
                }
            });
            firstBreakJobs.forEach((x) => {
                switch (x.redItem) {
                    case ('Y'): {
                        redJobs.push(x);
                        break;
                    }
                    default: {
                        normJobs.push(x);
                    }
                }
            });
            // sort by job number
            redPending.sort((a, b) => (a.jobNumber < b.jobNumber) ? -1 : 1);
            normPending.sort((a, b) => (a.jobNumber < b.jobNumber) ? -1 : 1);
            redJobs.sort((a, b) => (a.jobNumber < b.jobNumber) ? -1 : 1);
            normJobs.sort((a, b) => (a.jobNumber < b.jobNumber) ? -1 : 1);
            // sort items with a jobOrder assigned in ascending order and move the set to the front of the index
            redPending.sort((a, b) => (a.jobNumber === b.jobNumber) ? 0 : (a.jobOrder === 0) ? 1 : (b.jobOrder === 0) ? -1 :
                (a.jobOrder < b.jobOrder) ? -1 : 1);
            normPending.sort((a, b) => (a.jobNumber === b.jobNumber) ? 0 : (a.jobOrder === 0) ? 1 : (b.jobOrder === 0) ? -1 :
                (a.jobOrder < b.jobOrder) ? -1 : 1);
            redJobs.sort((a, b) => (a.jobNumber === b.jobNumber) ? 0 : (a.jobOrder === 0) ? 1 : (b.jobOrder === 0) ? -1 :
                (a.jobOrder < b.jobOrder) ? -1 : 1);
            normJobs.sort((a, b) => (a.jobNumber === b.jobNumber) ? 0 : (a.jobOrder === 0) ? 1 : (b.jobOrder === 0) ? -1 :
                (a.jobOrder < b.jobOrder) ? -1 : 1);
            const combinedList = redPending.concat(normPending, redJobs, normJobs, firstBreakUnassigned);

            const sortedIndex: number[] = [];
            for (let x = 0; x < combinedList.length; x++) {
                sortedIndex.push(combinedList[x].index);
            }
            setSortedIndexOrder(sortedIndex);
            // break into separate array for display and set sorted indeces
            const combinedListBreaks: number[] = [0];
            const tempSortedArray: IDMMedia[][] = [];
            for (let x = 1; x < combinedList.length; x++) {
                if ((combinedList[x].jobNumber !== combinedList[x-1].jobNumber) || ((combinedList[x].jobNumber ===
                    combinedList[x-1].jobNumber) && combinedList[x].opCode !== combinedList[x-1].opCode)) {
                    combinedListBreaks.push(x);
                }
            }
            // slice array by the break points
            for (let y = 0; y < combinedListBreaks.length; y++) {
                if (combinedListBreaks[y+1]) {
                    tempSortedArray.push(combinedList.slice(combinedListBreaks[y], combinedListBreaks[y+1]));
                } else {
                    tempSortedArray.push(combinedList.slice(combinedListBreaks[y]));
                }
            }
            // get the job sequencing for potential downloads' file default names
            const sequencing: number[] = [];
            tempSortedArray.forEach((x) => {
                x.forEach((y, index) => {
                    sequencing.push(index);
                });
            });
            setSortJobSeqBreakdown(sequencing);

            // now that we are sorted, create arrays of media items
            const mediaItemArray : ReactElement[][] = [];
            const allItemsArray : ReactElement[] = [];
            for (let x = 0; x < tempSortedArray.length; x++) {
                mediaItemArray.push([]);
                for (let y = 0; y < tempSortedArray[x].length; y++) {
                    const mediaItem = <div key = {'item'+tempSortedArray[x][y].index} className={css.mediaItem}>
                        <ServiceDocsMediaItem
                            // key = {'item'+tempSortedArray[x][y].index}
                            selectedRO = {props.selectedRO}
                            date = {tempSortedArray[x][y].date}
                            time = {tempSortedArray[x][y].time}
                            checked = {imageCheckArray[tempSortedArray[x][y].index]}
                            index = {tempSortedArray[x][y].index}
                            url = {tempSortedArray[x][y].url}
                            urlType = {tempSortedArray[x][y].urlType}
                            previewFlag = {props.previewFlag}
                            onToggle = {onToggle}
                            setFullImgFlag = {setFullImageFlag}
                            setCurrentFullImage = {setCurrentFullImage}
                            logDocsHistory = {props.logDocsHistory}
                            addCUC = {props.addCUC}
                        />
                    </div>;
                    mediaItemArray[x].push(mediaItem);
                }
                const finalItem = <div className={css.imageSection} key = {'finalItem'+x}>
                    <div className={imageJobStyles}>{tempSortedArray[x][0].opDesc}</div>
                    {allUnassigned ? null:<hr className={css.horizLine}/>}
                    <div className={css.imagePicArea}>
                        {mediaItemArray[x]}
                    </div>
                </div>;
                allItemsArray.push(finalItem);
            }
            setImageVidArray(allItemsArray);
        }
    }, [props.selectedRO, props.previewFlag, imageCheckArray, onToggle, props.logDocsHistory, props.addCUC, imageJobStyles]);

    const downloadBtnStyles = classNames(
        css.downloadBtn,
        {
            [css.downloadBtnBMW]: props.theme === 'bmw',
        },
    );

    const headerBoxStyles = classNames(
        css.headerbox,
        {
            [css.headerboxMazda]: props.theme === 'mazda',
            [css.headerboxGenesis]: props.theme === 'genesis',
            [css.headerboxAudi]: props.theme === THEME.AUDI,
        },
    );

    // The full-image gallery header, unlike the thumbnail header above, has never carried the
    // Mazda/Genesis classes - it was a bare css.headerbox. Reusing headerBoxStyles to add the
    // Audi class would have handed those two themes .headerboxMazda / .headerboxGenesis's
    // `letter-spacing: 2px` on a header that never had it, which is a visual change to two OEMs
    // this PMG does not cover. So the gallery gets its own list: the base class plus Audi only.
    const galleryHeaderBoxStyles = classNames(
        css.headerbox,
        {
            [css.headerboxAudi]: props.theme === THEME.AUDI,
        },
    );

    let pictureDialog = <div className={css.thumbnailWrapper}>
        <div className={headerBoxStyles}>
            {props.dialogTitle}
            {/* Audi: wrapper owns click and keyboard. Other themes: master's markup. */}
            {props.theme === THEME.AUDI ?
                <div className={classNames(css.headerIcon, css.headerIconAudi)}
                    onClick={() => props.setIVModalFlag(false)}
                    onKeyDown={thumbnailCloseKeyHandler}
                    role='button'
                    tabIndex={0}
                    aria-label={strings.Close}>
                    <IconRemoveAudi id='closeBtn'/>
                </div> :
                <div className={css.headerIcon}>
                    <IconRemove id='closeBtn' onClick={() => props.setIVModalFlag(false)}/>
                </div>}
        </div>
        {/* Date and info */}
        <div>
            <h2 className={css.imageDate}>{roDate}</h2>
            <h2 className={css.instructionMsg}>
                {strings.SelectThumbnail}
            </h2>
        </div>
        <ScrollBar scrollVertical>
            <div>
                {imageVidArray}
            </div>
        </ScrollBar>
        <div className={css.dialogFooter}>
            <div className={css.btnArea}>
                <div className={downloadBtnStyles}>
                    <Button
                        id='downloadBtn'
                        onClick={downloadHandler}
                        variant={'download'}
                        disabled={imageCheckArray.findIndex(check => check) === -1 || props.previewFlag === 'p'}
                    >
                        {strings.DownloadSelected}
                    </Button>
                </div>
            </div>
        </div>
    </div>;

    if (fullImageFlag && props.selectedRO.IDMPics && (currentFullImg || currentFullImg===0)) {
        // set the full screen mode's icon navigation for the images/videos
        const iconNavArray: iconNavItem[] = [];
        sortedIndexOrder.forEach((x) => {
            let iconFilled = false;
            if (x === currentFullImg) {
                iconFilled = true;
            }
            const iconNav: iconNavItem = {
                type: props.selectedRO!.IDMPics![x].urlType,
                iconFilled: iconFilled,
                onClick: () => {
                    setCurrentFullImage(x);
                    let logUrl = '';
                    if (props.selectedRO.IDMPics && props.selectedRO.IDMPics[x].url) {
                        logUrl = props.selectedRO.IDMPics[x].url;
                    }
                    if (!props.previewFlag) {
                        props.logDocsHistory(props.selectedRO.RONum, 'DV', 'IV', '', logUrl);
                    }
                    if (props.selectedRO.IDMPics && props.selectedRO.IDMPics[x].urlType === 'I' && !props.previewFlag) {
                        props.addCUC(869);
                    }
                },
            };
            iconNavArray.push(iconNav);
        });
        const navigation = <ServiceDocsMediaNav
            iconNav = {iconNavArray}
            currentImg = {currentFullImg}
            sortedOrder = {sortedIndexOrder}
        />;
        // check to see if we hide navigation arrows
        let hideLeftArrow = false;
        let hideRightArrow = false;
        const currentLocation = sortedIndexOrder.findIndex(result => result === currentFullImg);
        if (currentLocation === 0) {
            hideLeftArrow=true;
        }
        if (currentLocation === sortedIndexOrder.length-1) {
            hideRightArrow=true;
        }
        // set the updated date and time
        const upDateTime = strings.UpdatedColon +
            props.selectedRO.IDMPics[currentFullImg].time+' - '+props.selectedRO.IDMPics[currentFullImg].date;

        if (props.selectedRO.IDMPics[currentFullImg].urlType === 'I' || props.selectedRO.IDMPics[currentFullImg].urlType === 'V') {
            pictureDialog = <div className={css.galleryWrapper}>
                <div className={galleryHeaderBoxStyles}>
                    {props.dialogTitle}
                    {/* Display arrow on full display only */}
                    <div className={css.headerIconArrow}>
                        <IconBackArrow id='imageBackBtn' onClick={() => setFullImageFlag(false)}/>
                    </div>
                    {/* Audi: wrapper owns click and keyboard. Other themes: master's markup. */}
                    {props.theme === THEME.AUDI ?
                        <div className={classNames(css.headerIconClose, css.headerIconAudi)}
                            onClick={galleryCloseHandler}
                            onKeyDown={galleryCloseKeyHandler}
                            role='button'
                            tabIndex={0}
                            aria-label={strings.Close}>
                            <IconRemoveAudi id='closeBtn'/>
                        </div> :
                        <div className={css.headerIconClose}>
                            <IconRemove id='closeBtn' onClick={() => {
                                props.setIVModalFlag(false);
                                setFullImageFlag(false);
                            }}/>
                        </div>}
                </div>
                {/* Date and info */}
                <div className={css.imageFullHead}>
                    <div className={css.imageFullJob}>{props.selectedRO.IDMPics[currentFullImg].opDesc}</div>
                    {/* download icon */}
                    <div className={props.previewFlag === 'p'?
                        css.downloadIconDisabled:css.downloadIcon} id='downloadIcon' onClick={downloadFullHandler}>
                        <IconDownload/>
                    </div>
                </div>
                <ScrollBar scrollVertical>
                    <div>
                        <div id='imageSection' className={css.imageSection} onTouchStart={touchStartHandler}
                            onTouchMove={touchMoveHandler} onTouchEnd={touchEndHandler}>
                            <div className={css.imageFullPicArea}>
                                {
                                    hideLeftArrow ?
                                        <div className={css.fullImageLeftArrow}>
                                        </div>:
                                        <div className={css.fullImageLeftArrow} onClick={() => arrowMove('L')}>
                                            <IconLDropDownArrow/>
                                        </div>
                                }
                                {/* Check if image or video. */}
                                {props.selectedRO.IDMPics[currentFullImg].urlType === 'I' ?
                                    <img
                                        src={props.selectedRO.IDMPics[currentFullImg].url}
                                        alt={''}
                                        className={css.imgFullDisplay}
                                        onClick={() => null}>
                                    </img>:
                                    <div
                                        className={css.expandedMediaDisplayVideo}>
                                        {/* When the video plays be sure to increment the CUC */}
                                        <video
                                            className={css.expandedMediaDisplayVideo}
                                            id='docVideo'
                                            src={props.selectedRO.IDMPics[currentFullImg].url}
                                            title={'docVideo'}
                                            autoPlay={false}
                                            controls
                                            crossOrigin='anonymous'
                                            onPlay={() => {
                                                if (!props.previewFlag) {
                                                    props.addCUC(870);
                                                }
                                            }}>
                                            {props.selectedRO.IDMPics[currentFullImg].vttUrl ? <track
                                                default
                                                kind='captions'
                                                src={props.selectedRO.IDMPics[currentFullImg].vttUrl}/> : null}
                                        </video>
                                    </div>
                                }
                                {
                                    hideRightArrow ?
                                        <div className={css.fullImageRightArrow}>
                                        </div>:
                                        <div id='rightArrow' className={css.fullImageRightArrow} onClick={() => arrowMove('R')}>
                                            <IconRDropDownArrow/>
                                        </div>
                                }
                            </div>
                        </div>
                        <div className={css.dialogFooter}>
                            <div>{navigation}</div>
                            <div>{strings.Notes + ': ' +
                                (props.selectedRO.IDMPics[currentFullImg].note ?
                                    props.selectedRO.IDMPics[currentFullImg].note : '')}
                            </div>
                            <div>{upDateTime}</div>
                        </div>
                    </div>
                </ScrollBar>
                {/* <div className={css.dialogFooter}>
                    <div>{navigation}</div>
                    <div>{strings.Notes + ': ' +
                        (props.selectedRO.IDMPics[currentFullImg].note ? props.selectedRO.IDMPics[currentFullImg].note:'')}</div>
                    <div>{upDateTime}</div>
                </div> */}
            </div>;
        }
    }

    return (
        <div>
            <div>
                <Dialog
                    cssClasses={{
                        content: classNames(css.content, { [css.contentAudi]: props.theme === THEME.AUDI }),
                        paper: classNames(css.paper, { [css.paperAudi]: props.theme === THEME.AUDI }),
                        header: css.LightBoxHeader,
                    }}
                    isOpen={props.IVModalFlag}
                    anchorOrigin={{
                        vertical: 'top',
                    }}
                    header={strings.PicsVideos}
                    hideSave={true}
                    hideCancel={true}
                    disableUpperCase={true}
                    onCancel={() => props.setIVModalFlag(false)}
                    onBackDropClick={() => props.setIVModalFlag(false)}
                    width='full'
                >
                    {pictureDialog}
                </Dialog>
            </div>
            {
                saveAsOpenFlag ?
                    <SaveAs
                        downloadList={saveAsDownloadList}
                        defaultFileNames={saveAsDefaultFileNames}
                        closeHandler={saveAsCloseHandler}
                        theme={props.theme === THEME.AUDI ? props.theme : undefined}
                    />: null
            }
        </div>
    );
};

export default ServiceDocsMediaBox;